import * as React from 'react';
import { BackdropColor } from '../Backdrop';

import { PopupContent, PopupHeader, PopupWrapper } from './components';
import { buildReactStateHandler, makeDebugger } from '@lib';
import { PopupProps, PopupCloseContextTarget } from './types';
import { Subscription } from 'rxjs/internal/Subscription';
import { fromEvent } from 'rxjs/internal/observable/fromEvent';
import { filter } from 'rxjs/internal/operators/filter';

const debug = makeDebugger('Popup');
const stateHandler = buildReactStateHandler<PopupState>();

type PopupState = Readonly<{
  scrolling: boolean;
  hide: boolean;
}>;

export class Popup extends React.Component<PopupProps, PopupState> {
  static Content = PopupContent;
  static Header = PopupHeader;

  static defaultProps = {
    active: true,
    backdrop: true,
    backdropColor: 'dark' as BackdropColor,
    disableBackdropClick: false,
    disableEscapeKeydown: false,
    size: 'normal',
    node: document.body,
    closeIcon: true,
    hideOutterClose: false
  };

  private popupRef: React.RefObject<HTMLDivElement> = React.createRef();
  private escapeKeydownSubscription?: Subscription;

  readonly state: PopupState = {
    scrolling: false,
    hide: false
  };

  componentDidUpdate(prevProps: PopupProps) {
    if (this.props.open && !prevProps.open) {
      this.resetAnimationState();
      this.subscribeEventListeners();
    } else if (prevProps.open && !this.props.open) {
      this.finishCloseAnimation();
      this.unsubscribeEventListeners();
    }
  }

  // ----------------------------------------
  // Callback handling
  // ----------------------------------------

  handleOnCloseRequest: PopupProps['onClose'] = () => {
    debug('handleOnCloseRequest()');
    this.props.onClose();
  };

  // Handling animation flow

  /**
   * Reset close animation state when a modal goes from `closed` to `open`
   */
  private resetAnimationState = () => {
    this.setState(
      stateHandler(draft => {
        draft.hide = false;
      })
    );
  };

  /**
   * Animation has completed - now remove the node from the DOM
   */

  private finishCloseAnimation = () => {
    window.setTimeout(() => {
      this.setState(
        stateHandler(draft => {
          draft.hide = true;
        })
      );
    }, 500);
  };

  // ----------------------------------------
  // Event (un)subscriptions
  // ----------------------------------------

  subscribeEventListeners = () => {
    debug('subscribeEventListeners()');

    if (!this.props.disableEscapeKeydown) {
      this.observeEscapeKeydown();
    }
  };

  unsubscribeEventListeners = () => {
    debug('unsubscribeToEventSubscriptions()');

    if (this.escapeKeydownSubscription) {
      this.escapeKeydownSubscription.unsubscribe();
    }
  };

  observeEscapeKeydown = () => {
    this.escapeKeydownSubscription = fromEvent(document, 'keydown')
      .pipe(
        filter((e: any) => {
          return !this.props.disableEscapeKeydown && this.props.open && e.keyCode === 27;
        })
      )
      .subscribe(e => {
        debug('observeEscapeKeydown()');
        this.handleCloseRequest(e, 'escape');
      });
  };

  private handleCloseRequest = (e: React.MouseEvent<HTMLElement>, target: PopupCloseContextTarget) => {
    this.props.onClose();
  };

  // ----------------------------------------
  // Render
  // ----------------------------------------

  render() {
    const { hide } = this.state;
    const { open, children, disableEscapeKeydown, onClose } = this.props;

    const closed = !open && hide;
    if (closed) {
      return null;
    }

    return (
      <PopupWrapper
        open={open}
        onClose={onClose}
        forwardedRef={this.popupRef}
        disableEscapeKeydown={disableEscapeKeydown}
      >
        {children}
      </PopupWrapper>
    );
  }
}
